import type { Question } from '../models/Question';
import type { FieldSchema, QuestionOption, UploadedFile } from '../types';

function normalizeOptions(options: QuestionOption[] = []): QuestionOption[] {
  return options.map((option) => ({
    ...option,
    label: option.label || option.value,
  }));
}

function formatOptionAnswer(answer: unknown, options: QuestionOption[] = []): string {
  const normalized = normalizeOptions(options);
  const matches = normalized.filter((option) => option.value.toString() === String(answer));

  if (matches.length) {
    const match = matches[0];
    return match.label ? match.label : match.value;
  }

  return String(answer ?? '');
}

/**
 * Dependant fields keep their options under `dependantOptions`, keyed by the
 * answer of the field they depend on. Callers that know the parent answer
 * (see resolveDependantOptions) narrow that down to `options` first; otherwise
 * fall back to every option set so the answer still resolves to a label.
 */
function dependantOptions(fieldSchema?: FieldSchema): QuestionOption[] {
  if (fieldSchema?.options?.length) {
    return fieldSchema.options;
  }

  return Object.values(fieldSchema?.dependantOptions ?? {}).flat();
}

function formatCheckListAnswer(answer: unknown, options: QuestionOption[] = []): string {
  const normalized = normalizeOptions(options);
  const selected = String(answer ?? '')
    .split(', ')
    .filter(Boolean);

  return selected
    .map((value) => {
      const option = normalized.find((o) => o.value === value);
      return option?.label || value;
    })
    .filter(Boolean)
    .join(', ');
}

function formatFileUploadAnswer(answer: unknown): string {
  if (!Array.isArray(answer)) {
    return '';
  }

  return (answer as UploadedFile[])
    .map((file) => `${file.originalFileName} (${file.formattedFileSize})`)
    .join(', ');
}

const formatters: Record<string, (question: Question, fieldSchema?: FieldSchema) => string> = {
  TextArea: (question) => String(question.answer ?? '').replace(/\n/gi, '<br>'),
  Radio: (question, fieldSchema) => formatOptionAnswer(question.answer, fieldSchema?.options),
  RadioList: (question, fieldSchema) => formatOptionAnswer(question.answer, fieldSchema?.options),
  DropDown: (question, fieldSchema) => formatOptionAnswer(question.answer, fieldSchema?.options),
  DropDownDependant: (question, fieldSchema) =>
    formatOptionAnswer(question.answer, dependantOptions(fieldSchema)),
  RadioDependant: (question, fieldSchema) =>
    formatOptionAnswer(question.answer, dependantOptions(fieldSchema)),
  CheckList: (question, fieldSchema) => formatCheckListAnswer(question.answer, fieldSchema?.options),
  CheckBox: (question) => (question.answer === 'True' ? 'Yes' : ''),
  FileUpload: (question) => formatFileUploadAnswer(question.answer),
  Message: () => '',
};

export function formatNiceAnswer(question: Question, fieldSchema?: FieldSchema): string {
  const formatter = formatters[question.type];

  if (formatter) {
    return formatter(question, fieldSchema);
  }

  if (question.answer === null || question.answer === undefined) {
    return '';
  }

  return String(question.answer);
}

export function registerAnswerFormatter(
  type: string,
  formatter: (question: Question, fieldSchema?: FieldSchema) => string
): void {
  formatters[type] = formatter;
}
